/**
 * WHEP (WebRTC-HTTP Egress Protocol) Client
 * 
 * Handles WebRTC connection establishment for listener audio playback
 * using the WHEP protocol to communicate with the backend.
 */

export interface WhepClientConfig {
    sessionId: string;
    apiUrl: string;
    authToken: string;
    enableAutoReconnect?: boolean;
    maxReconnectAttempts?: number;
}

export interface WhepConnectionState {
    state: RTCPeerConnectionState;
    iceConnectionState: RTCIceConnectionState;
    iceGatheringState: RTCIceGatheringState;
    reconnecting?: boolean;
    reconnectAttempt?: number;
}

export type WhepConnectionStateCallback = (state: WhepConnectionState) => void;
export type WhepErrorCallback = (error: Error) => void;
export type WhepStreamCallback = (stream: MediaStream) => void;

export class WhepClient {
    private peerConnection: RTCPeerConnection | null = null;
    private config: WhepClientConfig;
    private connectionId: string | null = null;
    private onStateChangeCallback: WhepConnectionStateCallback | null = null;
    private onErrorCallback: WhepErrorCallback | null = null;
    private onStreamCallback: WhepStreamCallback | null = null;
    private receivedStream: MediaStream | null = null;
    private reconnectAttempts = 0;
    private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    private isReconnecting = false;
    private maxReconnectAttempts: number;
    private enableAutoReconnect: boolean;
    private streamTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(config: WhepClientConfig) {
        this.config = config;
        this.enableAutoReconnect = config.enableAutoReconnect ?? false;
        this.maxReconnectAttempts = config.maxReconnectAttempts ?? 3;
    }

    /**
     * Set callback for connection state changes
     */
    onStateChange(callback: WhepConnectionStateCallback): void {
        this.onStateChangeCallback = callback;
    }

    /**
     * Set callback for errors
     */
    onError(callback: WhepErrorCallback): void {
        this.onErrorCallback = callback;
    }

    /**
     * Set callback for receiving audio stream
     */
    onStream(callback: WhepStreamCallback): void {
        this.onStreamCallback = callback;
    }

    /**
     * Connect to session and receive audio stream using WHEP protocol
     */
    async connect(): Promise<MediaStream> {
        try {
            this.reconnectAttempts = 0;
            this.isReconnecting = false;

            const stream = await this.establishConnection();
            return stream;

        } catch (error) {
            // Clean up on error
            this.disconnect();
            throw error;
        }
    }

    /**
     * Establish WebRTC connection
     */
    private async establishConnection(): Promise<MediaStream> {
        // Create RTCPeerConnection with ICE servers
        // Note: Cloudflare Realtime Kit uses ice-lite, so we only need STUN servers
        this.peerConnection = new RTCPeerConnection({
            iceServers: [
                { urls: 'stun:stun.cloudflare.com:3478' },
            ],
            bundlePolicy: 'max-bundle',
            rtcpMuxPolicy: 'require',
            iceCandidatePoolSize: 10,
        });

        // Set up connection state listeners
        this.setupConnectionListeners();

        // Set up stream waiting BEFORE any SDP exchange to avoid race condition
        const streamPromise = this.waitForStream();

        // Add a transceiver for receiving audio
        // This is the modern way to receive media in WebRTC
        this.peerConnection.addTransceiver('audio', {
            direction: 'recvonly'
        });

        // Create SDP offer for receiving audio
        const offer = await this.peerConnection.createOffer();

        // Set local description
        await this.peerConnection.setLocalDescription(offer);

        // Wait for ICE gathering to complete
        await this.waitForIceGathering();

        // Get the complete SDP offer with ICE candidates
        const completeSdp = this.peerConnection.localDescription?.sdp;
        if (!completeSdp) {
            throw new Error('Failed to generate SDP offer');
        }

        // Send WHEP request to backend
        const sdpAnswer = await this.sendWhepRequest(completeSdp);

        // Set remote description with SDP answer
        console.log('Setting remote description (SDP answer)');
        await this.peerConnection.setRemoteDescription({
            type: 'answer',
            sdp: sdpAnswer,
        });
        console.log('Remote description set successfully');
        console.log('Current connection state:', {
            connectionState: this.peerConnection.connectionState,
            iceConnectionState: this.peerConnection.iceConnectionState,
            signalingState: this.peerConnection.signalingState
        });

        // Wait for audio stream to be received (promise was created before SDP exchange)
        console.log('Waiting for audio stream (promise created earlier)...');
        const stream = await streamPromise;
        console.log('Audio stream received!');

        return stream;
    }

    /**
     * Wait for ICE gathering to complete
     */
    private waitForIceGathering(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.peerConnection) {
                reject(new Error('No peer connection'));
                return;
            }

            // If already complete, resolve immediately
            if (this.peerConnection.iceGatheringState === 'complete') {
                resolve();
                return;
            }

            // Set timeout for ICE gathering (10 seconds)
            const timeout = setTimeout(() => {
                reject(new Error('ICE gathering timeout'));
            }, 10000);

            // Listen for ICE gathering state changes
            const handleIceGatheringStateChange = () => {
                if (this.peerConnection?.iceGatheringState === 'complete') {
                    clearTimeout(timeout);
                    this.peerConnection.removeEventListener(
                        'icegatheringstatechange',
                        handleIceGatheringStateChange
                    );
                    resolve();
                }
            };

            this.peerConnection.addEventListener(
                'icegatheringstatechange',
                handleIceGatheringStateChange
            );
        });
    }

    /**
     * Wait for audio stream to be received
     */
    private waitForStream(): Promise<MediaStream> {
        return new Promise((resolve, reject) => {
            if (!this.peerConnection) {
                reject(new Error('No peer connection'));
                return;
            }

            console.log('Setting up track event listener...');

            // Set timeout for receiving stream (20 seconds - increased for debugging)
            this.streamTimeout = setTimeout(() => {
                console.error('Stream reception timeout - no track event received');
                console.log('Final connection state:', {
                    connectionState: this.peerConnection?.connectionState,
                    iceConnectionState: this.peerConnection?.iceConnectionState,
                    signalingState: this.peerConnection?.signalingState
                });
                // Check if we have any receivers
                const receivers = this.peerConnection?.getReceivers() || [];
                console.log('Receivers:', receivers.map(r => ({
                    track: r.track?.kind,
                    trackId: r.track?.id,
                    trackState: r.track?.readyState
                })));
                this.streamTimeout = null;
                reject(new Error('Stream reception timeout'));
            }, 20000);

            // Listen for track events
            const handleTrack = (event: RTCTrackEvent) => {
                console.log('Track event handler called!');
                if (this.streamTimeout) {
                    clearTimeout(this.streamTimeout);
                    this.streamTimeout = null;
                }

                if (event.streams && event.streams.length > 0) {
                    const stream = event.streams[0];
                    this.receivedStream = stream;

                    console.log('Stream received successfully:', {
                        streamId: stream.id,
                        trackCount: stream.getTracks().length
                    });

                    // Notify stream callback
                    if (this.onStreamCallback) {
                        this.onStreamCallback(stream);
                    }

                    resolve(stream);
                } else {
                    console.error('Track event received but no streams attached');
                    reject(new Error('No stream received in track event'));
                }
            };

            this.peerConnection.addEventListener('track', handleTrack, { once: true });
            console.log('Track event listener registered');
        });
    }

    /**
     * Send WHEP request to backend
     */
    private async sendWhepRequest(sdpOffer: string): Promise<string> {
        const whepUrl = `${this.config.apiUrl}/rtc/sessions/${this.config.sessionId}/whep`;

        const response = await fetch(whepUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/sdp',
                'Authorization': `Bearer ${this.config.authToken}`,
            },
            body: sdpOffer,
        });

        if (!response.ok) {
            const errorText = await response.text();
            let errorMessage = 'Failed to establish WHEP connection';

            try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.error?.message || errorMessage;
            } catch {
                // If not JSON, use status text
                errorMessage = `${errorMessage}: ${response.statusText}`;
            }

            throw new Error(errorMessage);
        }

        // Extract connection ID from Location header
        const locationHeader = response.headers.get('Location');
        if (locationHeader) {
            const match = locationHeader.match(/\/whep\/([^/]+)$/);
            if (match) {
                this.connectionId = match[1];
            }
        }

        // Get SDP answer from response body
        const sdpAnswer = await response.text();
        if (!sdpAnswer || !sdpAnswer.includes('v=0')) {
            throw new Error('Invalid SDP answer received from server');
        }

        return sdpAnswer;
    }

    /**
     * Set up connection state listeners
     */
    private setupConnectionListeners(): void {
        if (!this.peerConnection) return;

        // Connection state change
        this.peerConnection.addEventListener('connectionstatechange', () => {
            this.notifyStateChange();

            // Handle connection failures
            if (this.peerConnection?.connectionState === 'failed') {
                console.error('WebRTC connection failed');
                this.handleConnectionFailure();
            }

            // Note: 'disconnected' state is temporary and can recover
            // Only treat 'failed' as a permanent failure
            if (this.peerConnection?.connectionState === 'disconnected') {
                console.warn('WebRTC connection temporarily disconnected, waiting for recovery...');
            }
        });

        // ICE connection state change
        this.peerConnection.addEventListener('iceconnectionstatechange', () => {
            this.notifyStateChange();

            if (this.peerConnection?.iceConnectionState === 'failed') {
                console.error('ICE connection failed');
                this.handleConnectionFailure();
            }

            if (this.peerConnection?.iceConnectionState === 'disconnected') {
                console.warn('ICE connection disconnected');
                // Don't trigger reconnection on ICE disconnected, wait for connection state
            }
        });

        // ICE gathering state change
        this.peerConnection.addEventListener('icegatheringstatechange', () => {
            this.notifyStateChange();
        });

        // ICE candidate events (for debugging)
        this.peerConnection.addEventListener('icecandidate', (event) => {
            if (event.candidate) {
                console.log('ICE candidate:', event.candidate.candidate);
            } else {
                console.log('ICE gathering complete');
            }
        });

        // Track events (for debugging)
        this.peerConnection.addEventListener('track', (event) => {
            console.log('Track event received!', {
                kind: event.track.kind,
                id: event.track.id,
                label: event.track.label,
                readyState: event.track.readyState,
                streams: event.streams.length
            });
        });
    }

    /**
     * Notify state change callback
     */
    private notifyStateChange(): void {
        if (this.onStateChangeCallback && this.peerConnection) {
            this.onStateChangeCallback({
                state: this.peerConnection.connectionState,
                iceConnectionState: this.peerConnection.iceConnectionState,
                iceGatheringState: this.peerConnection.iceGatheringState,
                reconnecting: this.isReconnecting,
                reconnectAttempt: this.reconnectAttempts,
            });
        }
    }

    /**
     * Handle connection failure and attempt reconnection
     */
    private handleConnectionFailure(): void {
        // Don't attempt reconnection if already reconnecting or disabled
        if (this.isReconnecting || !this.enableAutoReconnect) {
            console.warn('Connection failed. Auto-reconnect is disabled.');
            if (this.onErrorCallback) {
                this.onErrorCallback(new Error('Connection lost.'));
            }
            return;
        }

        // Don't reconnect if we've exceeded max attempts
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('Max reconnection attempts reached');
            if (this.onErrorCallback) {
                this.onErrorCallback(new Error('Connection lost. Maximum reconnection attempts reached.'));
            }
            return;
        }

        // Attempt reconnection with exponential backoff
        this.attemptReconnection();
    }

    /**
     * Attempt to reconnect with exponential backoff
     */
    private async attemptReconnection(): Promise<void> {
        if (this.isReconnecting) {
            return;
        }

        this.isReconnecting = true;
        this.reconnectAttempts++;

        // Calculate backoff delay: 1s, 2s, 4s
        const backoffDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 4000);

        console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${backoffDelay}ms`);

        // Notify state change
        this.notifyStateChange();

        // Wait for backoff delay
        await new Promise(resolve => {
            this.reconnectTimeout = setTimeout(resolve, backoffDelay) as ReturnType<typeof setTimeout>;
        });

        try {
            // Close existing connection
            if (this.peerConnection) {
                this.peerConnection.close();
                this.peerConnection = null;
            }

            // Establish new connection
            const stream = await this.establishConnection();

            // Notify stream callback with new stream
            if (this.onStreamCallback) {
                this.onStreamCallback(stream);
            }

            // Reset reconnection state on success
            this.isReconnecting = false;
            this.reconnectAttempts = 0;

            console.log('Reconnection successful');

        } catch (error) {
            console.error('Reconnection failed:', error);
            this.isReconnecting = false;

            // Try again if we haven't exceeded max attempts
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
                this.handleConnectionFailure();
            } else {
                if (this.onErrorCallback) {
                    this.onErrorCallback(new Error('Failed to reconnect after multiple attempts'));
                }
            }
        }
    }

    /**
     * Manually trigger reconnection
     */
    async reconnect(): Promise<MediaStream> {
        // Reset reconnection state
        this.reconnectAttempts = 0;
        this.isReconnecting = false;

        // Close existing connection
        if (this.peerConnection) {
            this.peerConnection.close();
            this.peerConnection = null;
        }

        // Establish new connection
        const stream = await this.establishConnection();

        return stream;
    }

    /**
     * Get current connection state
     */
    getConnectionState(): WhepConnectionState | null {
        if (!this.peerConnection) return null;

        return {
            state: this.peerConnection.connectionState,
            iceConnectionState: this.peerConnection.iceConnectionState,
            iceGatheringState: this.peerConnection.iceGatheringState,
        };
    }

    /**
     * Get connection ID
     */
    getConnectionId(): string | null {
        return this.connectionId;
    }

    /**
     * Get received audio stream
     */
    getStream(): MediaStream | null {
        return this.receivedStream;
    }

    /**
     * Check if connected
     */
    isConnected(): boolean {
        return this.peerConnection?.connectionState === 'connected';
    }

    /**
     * Disconnect and clean up resources
     */
    disconnect(): void {
        // Clear reconnection timeout
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }

        // Clear stream timeout
        if (this.streamTimeout) {
            clearTimeout(this.streamTimeout);
            this.streamTimeout = null;
        }

        // Reset reconnection state
        this.isReconnecting = false;
        this.reconnectAttempts = 0;

        if (this.peerConnection) {
            // Close peer connection
            this.peerConnection.close();
            this.peerConnection = null;
        }

        this.connectionId = null;
        this.receivedStream = null;
    }

    /**
     * Get peer connection statistics
     */
    async getStats(): Promise<RTCStatsReport | null> {
        if (!this.peerConnection) return null;
        return await this.peerConnection.getStats();
    }
}
